import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import TextEdit from "../../../../molecules/TextEdit";
import SelectTextField from "../../../../molecules/SelectTextField";
import appConfig from "../../../../../config/app-config";

export interface BusinessPartnerData {
    uuid: string;
    name: string;
    document: string;
    entity_type: string;
    id?: string;
    person_id?: string;
}

interface Props {
    value: BusinessPartnerData | null;
    onChange: (partner: BusinessPartnerData | null) => void;
    disabled?: boolean;
}

const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

interface SearchOption {
    value: string;
    label: string;
    document: string;
    entity_type: string;
}

const BusinessPartnerManager: React.FC<Props> = ({ value, onChange, disabled = false }) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
    const [selectedUuid, setSelectedUuid] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const handleSearch = (search: string) => {
        setSearchValue(search);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (abortController.current) abortController.current.abort();

        if (search.length < MIN_SEARCH_LENGTH) {
            setSearchOptions([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        debounceTimer.current = setTimeout(async () => {
            abortController.current = new AbortController();

            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());

                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${search}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY_PERSON || ''
                    },
                    signal: abortController.current.signal
                });

                if (!response.ok) return;

                const data = await response.json();
                const items: SearchOption[] = data.results.map((item: any) => {
                    const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                    const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                    const document = cnpjDoc?.number || cpfDoc?.number || '';
                    return {
                        value: item.uuid,
                        label: `${item.name} - ${document}`,
                        document,
                        entity_type: item.personType === 'LEGAL_PERSON' ? 'PJ' : 'PF'
                    };
                });
                setSearchOptions(items);

                if (items.length > 0) {
                    setSelectedUuid(items[0].value);
                }
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    toast.error('Erro ao buscar parceiro');
                }
            } finally {
                setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleInclude = () => {
        if (!selectedUuid) {
            toast.error("Selecione um parceiro");
            return;
        }

        const selected = searchOptions.find(o => o.value === selectedUuid);
        if (!selected) return;

        if (!selected.document || selected.document.trim() === '') {
            toast.error("Parceiro sem documento cadastrado");
            return;
        }

        onChange({
            uuid: selected.value,
            name: selected.label.split(' - ')[0],
            document: selected.document,
            entity_type: selected.entity_type
        });

        setSearchValue('');
        setSearchOptions([]);
        setSelectedUuid('');
    };

    const handleRemove = () => {
        onChange(null);
    };

    return (
        <div>
            <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Visão Cliente</h3>

            {!value && (
                <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="col-span-2">
                        <TextEdit
                            label="Buscar Parceiro"
                            value={searchValue}
                            onChange={(e) => handleSearch(e.target.value)}
                            disabled={disabled}
                        />
                        {isSearching && (
                            <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                Buscando...
                            </div>
                        )}
                    </div>
                    <div className="col-span-8">
                        <SelectTextField
                            id="business_partner_uuid"
                            label="Parceiro"
                            value={selectedUuid}
                            items={searchOptions}
                            onChange={(e) => setSelectedUuid(e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button type="button"
                            onClick={handleInclude}
                            className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={disabled || !selectedUuid}
                        >
                            Incluir
                        </button>
                    </div>
                </div>
            )}

            {value && (
                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Nome</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Documento</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Tipo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                    {value.name}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                    {value.document}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        value.entity_type === 'PJ'
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                                            : 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                    }`}>
                                        {value.entity_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                                    </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    <button type="button"
                                        onClick={handleRemove}
                                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={disabled}
                                    >
                                        Remover
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BusinessPartnerManager;
